<template>
  <DefaultLayout>
    <div class="container py-3">
      <article>
        <h2>How does it work?</h2>
        <p>
          Kingsland Blockchain Certificates are simply certificates written on Ethereum's blockchain.
          Having them written on a blockchain makes them very secure and easy to verify.
          The data is unchangeable once written.
        </p>

        <section>
          <h4>Building a Certificate</h4>
          <p>To create a certificate, it starts with a payload. Payload is a JSON data that contains the data of the certificate.</p>
          <p>Here's an example of Kingsland Certificate payload data:</p>

          <CodePreview>{{ sampleCertificate.payload }}</CodePreview>

          <ul>
            <li>
              <strong>@data</strong>
              <span> - certificate addressee information that will appear on the certificate</span>
            </li>
            <li>
              <strong>@institutionAddress</strong>
              <span> - smart contract address of institution (Kingsland); will validate the issuers</span>
            </li>
            <li>
              <strong>@issuers</strong>
              <span> - list of addresses (Ethereum wallet) that can add state to certificate to make it valid and official</span>
            </li>
            <li>
              <strong>@certificateVersion</strong>
              <span> - an internal versioning of the institution (Kingsland)</span>
            </li>
            <li>
              <strong>@signatories</strong>
              <span> - signatories' information that will appear on the certificate</span>
            </li>
            <li>
              <strong>@course</strong>
              <span> - course information that will appear on the certificate</span>
            </li>
            <li>
              <strong>@template</strong>
              <span> - institution's certificate display template</span>
            </li>
          </ul>

          <p>Once the payload is finalized, it will be hashed with <b>RIPEMD160</b> to produce an ethereum address.</p>
          <p>
            Removing the spaces on the JSON above and hashing it will produce
            <strong>{{ sampleCertificate.signature.payloadHash }}</strong>
          </p>

          <p>Here's the no-space version of the JSON above:</p>
          <CodePreview :show-expand="false">{{ compressedPayload }}</CodePreview>

          <p class="mt-4">The payload hash (<b>b3d55a46bec7590ffc98b37eaf0fd96da43215da</b>) will be the address of the certificate in the blockchain.
            It promotes data integrity to the payload, if a value was changed, it would not match the address anymore.</p>

          <p>Now, the JSON version of the certificate will be: </p>

          <CodePreview>{{ sampleCertificate }}</CodePreview>

          <ul>
            <li>
              <strong>signature.merkle</strong>
              <span> - will have a <a href="https://en.wikipedia.org/wiki/Merkle_tree">merkle tree</a> value if the certificate is part of a batch or group; this will be explained more on batching section</span>
            </li>
            <li>
              <strong>signature.payloadHash</strong>
              <span> - the hashed payload of the certificate; certificate address in the blockchain</span>
            </li>
            <li>
              <strong>payload</strong>
              <span> - the payload of the certificate; certificate data</span>
            </li>
          </ul>
        </section>

        <section>
          <h4>Issuing/Adding State to Certificates</h4>
          <p>
            Adding state to the certificate address will make the certificate valid and official.
            This is where the issuers and institution smart contract comes in.
          </p>

          <p>
            The issuers address must be valid and part of the institution in order to recognize their state update to the certificate. The institution smart contract will verify these addresses.
          </p>

          <p>To add/update state to certificate address, the issuers must execute a transaction in the blockchain by sending 0 ETH to the certificate address, and the transaction will have a JSON payload in form of hex.</p>

          <p>For example, to officially issue a certificate, the certificate address must have an <b>issuedAt</b> state: </p>
          <CodePreview :show-expand="false">{{ JSON.stringify({ issuedAt: 1620082220827 }, null, 2) }}</CodePreview>

          <p>The data that will be added to the transaction is (Hex version of the JSON):</p>
          <CodePreview :show-expand="false">7b226973737565644174223a313632303038323232303832377d</CodePreview>

          <p class="mt-5">Issuers can send/add state to the certificate address as many as they can. It will just append to the existing state. For example if the issuers sent another state <b>revokedOn</b>, then the certificate will be: </p>
          <CodePreview :show-expand="false">{{ JSON.stringify({ issuedAt: 1620082220827, revokedOn: 1621850945000 }, null, 2) }}</CodePreview>
        </section>

        <section>
          <h4>Batching (Merkle root as group address)</h4>
          <img src="./../../assets/merkle-animation.gif" height="480" />

          <p>Issuing a certificate to a batch of addressees is very expensive since the issuers will have to send state on each certificate address (payload hash) to make them valid and official. If there are 20 certificates, the issuers will have to create 20 transactions also.</p>

          <p>
            <a href="https://en.wikipedia.org/wiki/Merkle_tree">Merkle Tree</a> will solve the issue.
            Collecting a group of certificate addresses and putting them in a merkle tree will produce a merkle root which will act as the group address of the certificates in the Blockchain.
            This way, the issuers won't have to send state on each certificate addresses but just on the group address or the merkle root.
          </p>

          <p>For example, if the certificate above is a part of a batch: </p>

          <ul>
            <li v-for="item of sampleBatchAddresses" :key="item">
              <b v-if="item === sampleCertificate.signature.payloadHash">{{ item }}</b>
              <span v-else>{{ item }}</span>
            </li>
          </ul>

          <p>
            The addresses are not the leaves of the merkle tree yet. Here's the formula to get the leaf: <b>RIPEMD160(@issuers + payloadHash)</b>
          </p>

          <ul>
            <li v-for="item of sampleBatchLeaves" :key="item">
              {{ item }}
            </li>
          </ul>

          <p>Given the leaves above, the merkle value of <b>b3d55a46bec7590ffc98b37eaf0fd96da43215da</b> will be: </p>
          <CodePreview :show-expand="false">{{ sampleMerkle }}</CodePreview>

          <p>The final JSON version of the certificate is:</p>
          <CodePreview>{{ sampleCertificateWithMerkle }}</CodePreview>

          <p>Now, the issuers can either send state on the batch address (merkle root) or certificate address (payload hash). If the issuers sends state on the batch address, it will affect the state of all certificates in the batch, otherwise sending state to the certificate address (payload hash) will affect only the certificate.</p>
        </section>

        <section>
          <h4>Verifying Certificates</h4>
          <p>Following below is the steps on verifying the certificate:</p>
          <ul>
            <li>
              <h5>Certificate integrity validation</h5>
              <p>Checks the integrity of the payload by hashing the <b>payload</b> with <b>RIPEMD160</b> and compare it to <b>signature["payloadHash"]</b>. If the result did not match, then it means the payload was compromised.</p>
            </li>
            <li>
              <h5>Institution Contract Validation</h5>
              <p>Checks the validity of <b>payload["@institutionAddress"]</b>; Tests if the address is a smart contract and is recognized as valid institution by Kingsland's admin smart contract.</p>
            </li>
            <li>
              <h5>Issuers Validation</h5>
              <p>Checks if <b>payload["@issuers"]</b> is valid with the institution smart contract.</p>
            </li>

            <li>
              <h5>Merkle Hash State Validation</h5>
              <p>If <b>signature["merkle"]</b> is not null, it will check if the leaf is really a part of the root, using the proof.</p>
            </li>
            <li>
              <h5>Certificate Hash State Validation</h5>
              <p>Scans all the transaction sent to the <b>signature["payloadHash"]</b> and <b>signature["merkle"]["root"]</b> that came from the issuers address. After consolidating, the transactions will be sorted by the date they were successfully sent, and all of hex data will be decoded to JSON to be appended to the state.</p>
              <p>If the final state is empty then the certificate is not valid and official. Otherwise, it will look for the <b>revokeOn</b> or <b>expiresOn</b> state, if they exist then they will compared with time to check validity.</p>
            </li>
          </ul>
        </section>
      </article>
    </div>
  </DefaultLayout>
</template>

<script>
  import merklefruit from "merklefruit";
  import RIPEMD160 from "ripemd160";
  import sampleCertificate from "../../locals/sample-certificate.json";
  import CodePreview from "./CodePreview"

  export default {
    name: "HowDoesItWork",
    components: {
      CodePreview
    },
    data: () => ({
      sampleCertificate
    }),
    computed: {
      compressedPayload() {
        return JSON.stringify(this.sampleCertificate.payload);
      },
      sampleBatchAddresses() {
        return [
          this.sampleCertificate.signature.payloadHash,
          "e33d2364b9393ea97cce47dd853b6390ebd6802a",
          "02b4b506965030accf12842dced4931c6225d184",
          "18dd195724f534a39f1e2841410bcda0f0a53363"
        ];
      },
      sampleBatchLeaves() {
        return this.sampleBatchAddresses.map(address => {
          const issuers = this.sampleCertificate.payload["@issuers"];
          const unHashedLeaf = JSON.stringify(issuers) + address;
          const hash = new RIPEMD160();
          hash.update(unHashedLeaf);
          return hash.digest('hex');
        });
      },
      sampleMerkle() {
        const tree = merklefruit([...this.sampleBatchLeaves]);
        return {
          root: tree.root(),
          leaf: this.sampleBatchLeaves[0],
          proof: tree.proof(this.sampleBatchLeaves[0])
        };
      },
      sampleCertificateWithMerkle() {
        const value = JSON.parse(JSON.stringify(this.sampleCertificate));
        value.signature.merkle = this.sampleMerkle;
        return value;
      }
    }
  }
</script>

<style lang="scss" scoped>
  article {
    max-width: 850px;
  }

  section {
    margin-top: 3em;
  }
</style>